import { Form } from "@/components/ui/form";
import { Diff } from "@/features/FormCard/Diff";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";

import {
  AutocompleteField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { Label } from "@radix-ui/react-label";
import { dynamicFormConfig, dynamicFormFields } from "./form-conf";
import { TechnicalInfoCardStatic } from "./technical_info_card";
import { getFieldTranslations } from "../../a6/A6StaticData/translations";
import { Tables } from "@/gen/tableNames/tables";

export function N1PlansEditCard({
  id,
  client,
}: {
  id: number;
  client: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    options,
    handleDelete,
  } = useDynamicForm({
    id,
    client,
    table_name: `${Tables.N1_PLANS}`,
    formConfig: dynamicFormConfig,
    adjustOnReset: (_key, value) => {
      return value;
    },
  });

  const fieldTranslations = getFieldTranslations(t);

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="font-bold text-gray-700">
                  {t(`${Tables.N1_PLANS}.title`, {
                    ns: "tables",
                    defaultValue: "Actif",
                  })}{" "}
                  {id}
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-4">
                  <Label>
                    {t(`${Tables.N1_PLANS}.sections.characteristics`, {
                      ns: "tables",
                    })}
                  </Label>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-12">
                      <InputField
                        name={dynamicFormFields.name}
                        label={t(`${Tables.N1_PLANS}.columns.name`, {
                          ns: "tables",
                        })}
                        control={form.control}
                      />
                    </div>
                    <AutocompleteField
                      className="md:col-span-6"
                      name={dynamicFormFields.asset_class_1}
                      label={t(`${Tables.N1_PLANS}.columns.asset_class_1`, {
                        ns: "tables",
                      })}
                      options={options?.[dynamicFormFields.asset_class_1] ?? []}
                      control={form.control}
                    />
                    <AutocompleteField
                      className="md:col-span-6"
                      name={dynamicFormFields.asset_class_2}
                      label={t(`${Tables.N1_PLANS}.columns.asset_class_2`, {
                        ns: "tables",
                      })}
                      options={options?.[dynamicFormFields.asset_class_2] ?? []}
                      control={form.control}
                    />

                    <div className="md:col-span-6">
                      <SelectField
                        name={dynamicFormFields.product}
                        label={t(`${Tables.N1_PLANS}.columns.product`, {
                          ns: "tables",
                        })}
                        options={(options[dynamicFormFields.product] ?? []).map(
                          (option) => ({
                            value: option.value,
                            label: option.value,
                          }),
                        )}
                        control={form.control}
                        allow_empty_option
                      />
                    </div>
                    <InputField
                      className="md:col-span-6"
                      name={dynamicFormFields.vehicle}
                      label={t(`${Tables.N1_PLANS}.columns.vehicle`, {
                        ns: "tables",
                      })}
                      control={form.control}
                    />
                    <AutocompleteField
                      className="md:col-span-6"
                      name={dynamicFormFields.sector}
                      label={t(`${Tables.N1_PLANS}.columns.sector`, {
                        ns: "tables",
                      })}
                      options={options?.[dynamicFormFields.sector] ?? []}
                      control={form.control}
                    />
                    <AutocompleteField
                      className="md:col-span-6"
                      name={dynamicFormFields.region}
                      label={t(`${Tables.N1_PLANS}.columns.region`, {
                        ns: "tables",
                      })}
                      options={options?.[dynamicFormFields.region] ?? []}
                      control={form.control}
                    />
                    <AutocompleteField
                      className="md:col-span-6"
                      name={dynamicFormFields.lpt_liquidity}
                      label={t(`${Tables.N1_PLANS}.columns.lpt_liquidity`, {
                        ns: "tables",
                      })}
                      options={options?.[dynamicFormFields.lpt_liquidity] ?? []}
                      control={form.control}
                    />

                    <div className="md:col-span-6">
                      <DatePickerField
                        name={dynamicFormFields.lpt_fund_vintage_start_date}
                        label={t(
                          `${Tables.N1_PLANS}.columns.lpt_fund_vintage_start_date`,
                          {
                            ns: "tables",
                          },
                        )}
                        control={form.control}
                      />
                    </div>
                    <div className="md:col-span-6">
                      <InputField
                        name={dynamicFormFields.commitment}
                        label={t(`${Tables.N1_PLANS}.columns.commitment`, {
                          ns: "tables",
                        })}
                        type="number"
                        control={form.control}
                      />
                    </div>

                    <div className="md:col-span-6">
                      <DatePickerField
                        name={dynamicFormFields.lpt_investment_period_end_date}
                        label={t(
                          `${Tables.N1_PLANS}.columns.lpt_investment_period_end_date`,
                          {
                            ns: "tables",
                          },
                        )}
                        control={form.control}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-6"></div>
                    {/* INTENTIONALLY LEFT BLANK */}
                  </div>

                  <Label>
                    {t(`${Tables.N1_PLANS}.sections.model_assumptions`, {
                      ns: "tables",
                    })}
                  </Label>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <InputField
                      className="md:col-span-12"
                      name={dynamicFormFields.lpt_investment_duration}
                      type="number"
                      label={t(
                        `${Tables.N1_PLANS}.columns.lpt_investment_duration`,
                        {
                          ns: "tables",
                        },
                      )}
                      control={form.control}
                    />
                    <InputField
                      name={dynamicFormFields.lpt_net_percentage_capital_called}
                      className="md:col-span-12"
                      type="number"
                      label={t(
                        `${Tables.N1_PLANS}.columns.lpt_net_percentage_capital_called`,
                        {
                          ns: "tables",
                        },
                      )}
                      control={form.control}
                    />
                    <InputField
                      className="md:col-span-12"
                      name={dynamicFormFields.lpt_loan_to_value}
                      type="number"
                      label={t(`${Tables.N1_PLANS}.columns.lpt_loan_to_value`, {
                        ns: "tables",
                      })}
                      control={form.control}
                    />
                    <div className="md:col-span-12">
                      <InputField
                        className="md:col-span-12"
                        name={dynamicFormFields.lpt_expected_distribution_rate}
                        type="number"
                        label={t(
                          `${Tables.N1_PLANS}.columns.lpt_expected_distribution_rate`,
                          {
                            ns: "tables",
                          },
                        )}
                        control={form.control}
                      />
                      <InputField
                        className="md:col-span-12"
                        name={dynamicFormFields.lpt_expected_investment_return}
                        type="number"
                        label={t(
                          `${Tables.N1_PLANS}.columns.lpt_expected_investment_return`,
                          {
                            ns: "tables",
                          },
                        )}
                        control={form.control}
                      />
                    </div>
                  </div>
                </CardBody>
                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
            children={
              changedFields
                ? Diff(
                    changedFields,
                    [],
                    fieldTranslations,
                    `${Tables.N1_PLANS}`,
                  )
                : t("no_changes")
            }
          />

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
      <TechnicalInfoCardStatic
        id={Number(id)}
        table_name={`${Tables.N1_PLANS}`}
        strategy_id_control={form.control}
        client={client}
      />
    </>
  );
}
